package koala.model

import kampfire.model.GeoRect
import kampfire.model.GeoPoint
import kampfire.model.refine
import kampfire.model.storeOf
import kampfire.model.tapOf
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.launch

/**
 * The camera over the map: its center, view and zoom as the map reports them, and requests to pan it.
 *
 * A settled state is one the camera has stopped moving on.
 */
class GeoCamera(
    private val scope: CoroutineScope,
) {
    private val state = storeOf(GeoCameraState())
    val stateFlow = state.flow
    val stateNow get() = state.now

    internal val panFlow: SharedFlow<PanPoint> field = MutableSharedFlow<PanPoint>(1)
    internal val panBoundsFlow: SharedFlow<GeoRect> field = MutableSharedFlow<GeoRect>(1)
    internal val movementFlow: Flow<MarkerMovement> field = MutableSharedFlow<MarkerMovement>(1)

    val pointAndZoom = state.tapOf { it.center to it.zoom }
    val viewedState = state.refine { states -> states.filter { it.isViewed } }
    val settledState = viewedState.refine { states -> states.filter { !it.isMoving } }

    val isMovingState = viewedState.tapOf { it.isMoving }
    val movingViewState = viewedState.tapOf { it.view }
    val focusState = viewedState.tapOf { it.focus }
    val zoomState = settledState.tapOf { it.zoom }
    val centerState = settledState.tapOf { it.center }
    val settledViewState = settledState.tapOf { it.view }

    /** Pans the map to [point]. */
    fun panMap(point: GeoPoint) {
        panMap(PanPoint(point))
    }

    /** Fits the map to [bounds]. */
    fun panMap(bounds: GeoRect) {
        scope.launch {
            panBoundsFlow.emit(bounds)
        }
    }

    /** Pans the map as [pan] asks. Does nothing when the camera is already there. */
    fun panMap(pan: PanPoint) {
        if (pan.point.isTouching(stateNow.center) && (pan.zoom == null || pan.zoom == stateNow.zoom)) return
        scope.launch {
            panFlow.emit(pan)
        }
    }

    internal fun setIsViewed(value: Boolean) {
        state.set { copy(isViewed = value) }
    }

    internal fun setBounds(center: GeoPoint, bounds: GeoRect, zoom: Float, isMoving: Boolean) {
        state.set { copy(center = center, view = bounds, zoom = zoom, isMoving = isMoving) }
    }
}

/** The camera's position, whether it is moving, whether the map is in view, and the focused marker. */
data class GeoCameraState(
    val center: GeoPoint = GeoPoint.Denver,
    val view: GeoRect = GeoRect.Denver,
    val zoom: Float = 11f,
    val isMoving: Boolean = true,
    val isViewed: Boolean = false,
    val focus: PointMarker? = null,
)

/** A point to pan to, with an optional [zoom]. A [snap] pan jumps there without animating. */
data class PanPoint(
    val point: GeoPoint,
    val zoom: Float? = null,
    val snap: Boolean = false,
)